import { useState } from "react";
import styled from "@emotion/styled";

import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import { EventData, TrackingProvider } from "lib/tracking";
import { LeadSortBy, LeadSortOrder } from "lib/types/graphQLEnums";
import { DocumentFindLeadsMetadata } from "../../../lib/hooks/api/leads/useCreateLeadOneClick";
import { useInfiniteLeads } from "../../../lib/hooks/api/leads/useLeads";
import { Flex, Text } from "../../../styles/utility-components";
import { isBuyerOrganisation, SharedSaveProps } from "../../entities/utils";
import { CreateLeadFormOutput } from "../create_lead/utils";
import { MAX_MATCHING_BUYERS, SaveToLead } from "./SaveToLead";

type Props = SharedSaveProps<CreateLeadFormOutput> & {
  trackingData: EventData;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  savedLeadIds: string[];
  buyerMatchOnly?: boolean;
  findLeadsMetadata?: DocumentFindLeadsMetadata;
  onLeadSaved?: (leadId: string) => void;
};

/**
 * Slideout drawer which opens and allows us to save an entity to a lead
 */
export default function SaveLeadDrawer({
  isOpen,
  trackingData,
  onOpenChange,
  entityType,
  entityId,
  defaultValues,
  savedLeadIds,
  buyerMatchOnly,
  findLeadsMetadata,
  onLeadSaved,
}: Props) {
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);

  const buyerIds =
    defaultValues?.entities?.filter(isBuyerOrganisation)?.map((e) => e.entityId) ?? [];

  // Required to render a custom drawer title when buyerMatchOnly is true
  const { data: buyerLeads } = useInfiniteLeads(
    {
      orderBy: LeadSortBy.BuyerMatching,
      order: LeadSortOrder.Desc,
      orderBuyerMatchingIds: buyerIds,
      buyerFilters: { buyers: buyerIds },
    },
    MAX_MATCHING_BUYERS,
    { enabled: !!buyerMatchOnly && buyerIds.length > 0 },
  );

  const buyerLeadCount = buyerLeads?.pages[0].leads.total ?? 0;

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} width={486} padding={24}>
      <TrackingProvider data={{ "Data type": entityType.toUpperCase() }}>
        <DrawerContent aria-label={`Save this ${entityType} to a lead`}>
          <Flex justifyContent="space-between" alignItems="flex-start">
            {isCreateFormOpen ? (
              <Button
                as="button"
                buttonType="secondary"
                icon="arrowNarrowLeft"
                onClick={() => setIsCreateFormOpen(false)}
              />
            ) : buyerMatchOnly ? (
              <Text h2>
                {buyerLeadCount} existing {buyerLeadCount === 1 ? "lead" : "leads"} identified
              </Text>
            ) : (
              <Text h3>Save to a lead</Text>
            )}
            <Button
              as="button"
              buttonType="secondary"
              icon="xClose"
              onClick={() => onOpenChange(false)}
            />
          </Flex>
          <Flex column height="100%">
            <SaveToLead
              trackingData={trackingData}
              entityType={entityType}
              entityIds={[entityId]}
              defaultValues={defaultValues}
              isCreateFormOpen={isCreateFormOpen}
              onToggleCreateForm={setIsCreateFormOpen}
              savedLeadIds={savedLeadIds}
              buyerMatchOnly={buyerMatchOnly}
              findLeadsMetadata={findLeadsMetadata}
              onLeadSaved={onLeadSaved}
            />
          </Flex>
        </DrawerContent>
      </TrackingProvider>
    </Drawer>
  );
}

const DrawerContent = styled(Flex)({
  flexDirection: "column",
  height: "100%",
  gap: 24,
  maxWidth: "440px",
});
